import React, { useMemo } from "react";
import PropTypes from "prop-types";
import ReactEcharts from "echarts-for-react";
import { Box, Card, CardContent, CircularProgress, Grid, Typography } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      borderRadius: 8,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      border: "1px solid #e5e7eb",
    },
    content: {
      padding: theme.spacing(2),
      "&:last-child": {
        paddingBottom: theme.spacing(2),
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    title: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1f2937",
    },
    subtitle: {
      fontSize: 12,
      color: "#6b7280",
      whiteSpace: "nowrap",
    },
    legend: {
      display: "grid",
      gap: 8,
      marginTop: 8,
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      fontSize: 12,
      color: "#4b5563",
    },
    legendLeft: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
    },
    swatch: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
    },
    name: {
      fontWeight: 600,
    },
    value: {
      fontWeight: 700,
      color: "#111827",
      whiteSpace: "nowrap",
    },
    chart: {
      height: 260,
      width: "100%",
    },
    loadingWrap: {
      height: 260,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  })
);

const formatNumber = (value) => new Intl.NumberFormat("ja-JP").format(value);

const PlotAssetBreakdown = ({ items, title, subtitle, loading }) => {
  const classes = useStyles();

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },
      legend: {
        show: false,
      },
      series: [
        {
          name: title,
          type: "pie",
          radius: ["56%", "78%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          data: items.map((item) => ({
            name: item.name,
            value: item.value,
            itemStyle: { color: item.color },
          })),
        },
      ],
    }),
    [items, title]
  );

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <div className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </div>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={7}>
            {loading ? (
              <Box className={classes.loadingWrap}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <ReactEcharts option={option} className={classes.chart} />
            )}
          </Grid>
          <Grid item xs={12} sm={5}>
            <div className={classes.legend}>
              {loading ? (
                <Box className={classes.loadingWrap} style={{ height: 260 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                items.map((item) => (
                  <div key={item.name} className={classes.legendItem}>
                    <div className={classes.legendLeft}>
                      <span className={classes.swatch} style={{ background: item.color }} />
                      <span className={classes.name}>{item.name}</span>
                    </div>
                    <span className={classes.value}>{formatNumber(item.value)}</span>
                  </div>
                ))
              )}
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

PlotAssetBreakdown.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
};

PlotAssetBreakdown.defaultProps = {
  title: "アセット種別 内訳",
  subtitle: "全体",
  loading: false,
};

export default PlotAssetBreakdown;
